import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import useForm from "../../hooks/useForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faCalculator, faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  utilsService,
  CurrencyObject,
  LocationInfo,
} from "../../services/utils.service";
import { rateService, RatePayload, RateOption } from "../../services/rate.service";
import { countryService, Country } from "../../services/country.service";
import AddressAutocomplete, { AddressComponents } from "../common/AddressAutocomplete";
import { normalizeZip, isValidZipFormat, getZipFormatErrorMessage } from "../../config/zip.utils";
import "../../styles/utility/CalculatorForm.css";
import btnStyles from "../../styles/components/Button.module.css";
import Swal from "sweetalert2";

import usePackageValidation, { validateWeightValue, validateDimensionValue } from "../../hooks/usePackageValidation";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const day = String(tomorrow.getDate()).padStart(2, '0');
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const year = tomorrow.getFullYear();
  return `${year}-${month}-${day}`;
};

const getMaxDate = () => {
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 7);
  const day = String(maxDate.getDate()).padStart(2, '0');
  const month = String(maxDate.getMonth() + 1).padStart(2, '0');
  const year = maxDate.getFullYear();
  return `${year}-${month}-${day}`;
};


type UnitSystem = "metric" | "imperial";
type Currency = string;

export interface CalculatorFormData {
  idCarrier: number;
  iso2Origin: string;
  iso2Destination: string;
  height: number;
  width: number;
  length: number;
  weight: number;
  unitSystem: string;
  zipcodeOrigin: string;
  zipcodeDestination: string;
  addressLineOrigin: string;
  addressLineDestination?: string;
  cityOrigin: string;
  stateOrigin: string;
  pickupDate: string;
  fob?: number;
  currency?: string;
  parcelOrdDoc?: string;
  serviceType?: string;
  hasBattery?: boolean;
  cityDestiny?: string;
  stateDestiny?: string;
  municipalityDestiny?: string;
  packages?: Array<{
    length: number;
    width: number;
    height: number;
    weight: number;
    fob?: number;
    content?: string;
    isFragile?: boolean;
  }>;
}

interface CalculatorFormProps {
  onCalculate: (results: RateOption[], formData: CalculatorFormData) => void;
}

const CalculatorForm: React.FC<CalculatorFormProps> = ({ onCalculate }) => {
  const [currencies, setCurrencies] = useState<CurrencyObject[]>([]);
  const [originLocationData, setOriginLocationData] = useState<LocationInfo | null>(null);
  const [destinationLocationData, setDestinationLocationData] = useState<LocationInfo | null>(null);
  const [zipcodeOriginApiError, setZipcodeOriginApiError] = useState<string | null>(null);
  const [zipcodeDestinationApiError, setZipcodeDestinationApiError] = useState<string | null>(null);
  const [allReceiverCountries, setAllReceiverCountries] = useState<Country[]>([]);
  const [mpReceiverCountries, setMpReceiverCountries] = useState<Country[]>([]);
  const [packages, setPackages] = useState<Array<{
    length: string;
    width: string;
    height: string;
    weight: string;
    fob: string;
    isFragile?: boolean;
    errors?: {
      length?: string | null;
      width?: string | null;
      height?: string | null;
      weight?: string | null;
      fob?: string | null;
    };
  }>>([{ length: "", width: "", height: "", weight: "", fob: "", isFragile: false }]);
  const receiverCountries = packages.length > 1 ? mpReceiverCountries : allReceiverCountries;

  const handleAddPackage = () => {
    if (packages.length >= 10) {
      Swal.fire({
        icon: "warning",
        title: "Límite alcanzado",
        text: "El número máximo de paquetes (MP) permitido es 10.",
        confirmButtonColor: "#3085d6",
        returnFocus: false
      });
      return;
    }
    if (values.destination) {
      const isDestinationMpCompatible = mpReceiverCountries.some(
        c => c.iso2Country.toUpperCase() === values.destination.toUpperCase()
      );
      if (!isDestinationMpCompatible) {
        Swal.fire({
          icon: "warning",
          title: "Destino incompatible",
          text: "El país de destino seleccionado no soporta envíos multipieza. Por favor, selecciona otro destino o mantén una sola pieza.",
          confirmButtonColor: "#3085d6",
          returnFocus: false
        });

        // Reset destination fields
        setValues((prev: any) => ({
          ...prev,
          destination: "",
          zipcodeDestination: "",
          stateDestiny: "",
          municipalityDestiny: "",
          addressLineDestination: "",
        }));
        setDestinationLocationData(null);

        // Scroll to destination selection field
        setTimeout(() => {
          const destSelect = document.getElementById("destination");
          if (destSelect) {
            destSelect.scrollIntoView({ behavior: "smooth", block: "center" });
            destSelect.focus();
          }
        }, 100);
      }
    }
    setPackages(prev => [...prev, { length: "", width: "", height: "", weight: "", fob: "", isFragile: false }]);
  };

  const handleRemovePackage = (index: number) => {
    setPackages(prev => prev.filter((_, i) => i !== index));
  };

  const handlePackageChange = (index: number, field: string, value: any) => {
    let finalValue = value;
    if (['length', 'width', 'height', 'weight', 'fob'].includes(field) && typeof value === 'string') {
      finalValue = value.replace(/-/g, '');
    }
    setPackages(prev => prev.map((pkg, i) => {
      if (i === index) {
        return { ...pkg, [field]: finalValue, errors: { ...pkg.errors, [field]: null } };
      }
      return pkg;
    }));
  };

  // Initial values for useForm
  const initialValues = {
    unitSystem: "imperial" as UnitSystem,
    currency: "" as Currency,
    origin: "US",
    destination: "",
    weight: "" as number | "",
    width: "" as number | "",
    height: "" as number | "",
    length: "" as number | "",
    fob: "" as number | "",
    zipcodeOrigin: "",
    zipcodeDestination: "",
    addressLineOrigin: "",
    addressLineDestination: "",
    municipalityDestiny: "",
    cityOrigin: "",
    stateOrigin: "",
    stateDestiny: "",
    pickupDate: getTomorrowDate(),
    parcelOrdDoc: "PARCEL",
    serviceType: "A",
    hasBattery: false,
  };

  // Validation rules
  const validationRules: any = {
    weight: (value: any, allValues: any) =>
      allValues?.parcelOrdDoc === "DOC" ? validateWeightValue(value, allValues?.unitSystem) : null,
    width: (value: any, allValues: any) =>
      allValues?.parcelOrdDoc === "DOC" ? validateDimensionValue(value, "Ancho", allValues?.unitSystem) : null,
    height: (value: any, allValues: any) =>
      allValues?.parcelOrdDoc === "DOC" ? validateDimensionValue(value, "Alto", allValues?.unitSystem) : null,
    length: (value: any, allValues: any) =>
      allValues?.parcelOrdDoc === "DOC" ? validateDimensionValue(value, "Largo", allValues?.unitSystem) : null,
    fob: (value: any, allValues: any) =>
      allValues?.parcelOrdDoc === "DOC" ? (value === "" || Number(value) < 0 ? "Requerido (>= 0)" : null) : null,
    zipcodeOrigin: (value: any) => (!value ? "Requerido" : null),
    zipcodeDestination: (value: any, allValues: any) => (allValues?.destination === "US" ? (!value ? "Requerido" : null) : null),
    addressLineOrigin: (value: any) => (!value ? "Requerido" : null),
    addressLineDestination: (value: any, allValues: any) => (allValues?.destination === "US" ? (!value ? "Requerido" : null) : null),
  };

  const { values, errors, handleChange, handleSubmit, setValues, setErrors } = useForm(
    initialValues,
    validationRules
  );

  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const data = await utilsService.getCurrencies();
        setCurrencies(data);
        if (data.length > 0) {
          const usd = data.find((c) => c.isO3Currency === "USD");
          if (usd) {
            setValues((prev: any) => ({ ...prev, currency: "USD" }));
          }
        }
      } catch (error) {
        console.error("Error cargando monedas", error);
      }
    };
    loadCurrencies();
  }, [setValues]);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const countryParam = searchParams.get("country");
    const weightParam = searchParams.get("weight");

    if (countryParam || weightParam) {
      const sanitizedCountry = countryParam?.toUpperCase() === "CU" ? "" : (countryParam || "");
      setValues((prev: any) => ({
        ...prev,
        destination: sanitizedCountry || prev.destination,
        weight: weightParam ? Number(weightParam) || "" : prev.weight,
      }));
    }
  }, [searchParams, setValues]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const resAll = await countryService.getCountryLists();
        const resMp = await countryService.getCountryListsMp();
        setAllReceiverCountries(resAll.receivers);
        setMpReceiverCountries(resMp.receivers);
      } catch (error) {
        console.error("Error fetching country lists in CalculatorForm:", error);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (!values.destination) {
      setErrors((prev: any) => ({ ...prev, destination: undefined }));
      return;
    }
    const country = receiverCountries.find((c) => c.iso2Country === values.destination);
    if (!country) return;
    if (values.serviceType === "A" && !country.air) {
      setErrors((prev: any) => ({
        ...prev,
        destination: "Servicio Aéreo no disponible para este destino",
      }));
    } else if (values.serviceType === "M" && !country.maritime) {
      setErrors((prev: any) => ({
        ...prev,
        destination: "Servicio Marítimo no disponible para este destino",
      }));
    } else {
      setErrors((prev: any) => ({ ...prev, destination: undefined }));
    }
  }, [values.destination, values.serviceType, receiverCountries, setErrors]);

  const handleCalculate = async () => {
    const mappedPackages = values.parcelOrdDoc === "DOC"
      ? [{ height: Number(values.height) || 0, width: Number(values.width) || 0, length: Number(values.length) || 0, weight: Number(values.weight) || 0, fob: Number(values.fob) || 0, isFragile: false }]
      : packages.map(pkg => ({
          height: Number(pkg.height) || 0,
          width: Number(pkg.width) || 0,
          length: Number(pkg.length) || 0,
          weight: Number(pkg.weight) || 0,
          fob: Number(pkg.fob) || 0,
          isFragile: Boolean(pkg.isFragile),
        }));

    const isUS = values.destination === "US";

    const payload: RatePayload = {
      iso2Origin: values.origin,
      iso2Destination: values.destination,
      serviceType: values.serviceType,
      height: mappedPackages[0]?.height || 0,
      width: mappedPackages[0]?.width || 0,
      length: mappedPackages[0]?.length || 0,
      weight: mappedPackages.reduce((acc: number, p: any) => acc + p.weight, 0),
      parcelOrdDoc: values.parcelOrdDoc,
      currency: values.currency,
      unitSystem: values.unitSystem === "metric" ? "METRIC" : "IMPERIAL",
      fob: mappedPackages.reduce((acc: number, p: any) => acc + (p.fob || 0), 0),
      zipcodeOrigin: values.zipcodeOrigin,
      zipcodeDestination: isUS ? values.zipcodeDestination : (null as any),
      addressLineOrigin: values.addressLineOrigin,
      addressLineDestination: isUS ? values.addressLineDestination : (null as any),
      pickupDate: values.pickupDate,
      cityOrigin: originLocationData?.city || values.cityOrigin || "",
      stateOrigin: originLocationData?.state || values.stateOrigin || "",
      stateDestiny: isUS ? (destinationLocationData?.state || values.stateDestiny || null) : (null as any),
      municipalityDestiny: isUS ? (values.municipalityDestiny || null) : (null as any),
      externalCarrierName: "UPS",
      packages: mappedPackages,
    };

    const formData: CalculatorFormData = {
      idCarrier: 0, // will be set by parent when user selects a carrier
      iso2Origin: values.origin,
      iso2Destination: values.destination,
      height: mappedPackages[0]?.height || 0,
      width: mappedPackages[0]?.width || 0,
      length: mappedPackages[0]?.length || 0,
      weight: mappedPackages.reduce((acc: number, p: any) => acc + p.weight, 0),
      unitSystem: values.unitSystem === "metric" ? "METRIC" : "IMPERIAL",
      zipcodeOrigin: values.zipcodeOrigin,
      zipcodeDestination: isUS ? values.zipcodeDestination : "",
      addressLineOrigin: values.addressLineOrigin,
      addressLineDestination: isUS ? values.addressLineDestination : "",
      cityOrigin: originLocationData?.city || values.cityOrigin || "",
      stateOrigin: originLocationData?.state || values.stateOrigin || "",
      pickupDate: values.pickupDate,
      fob: mappedPackages.reduce((acc: number, p: any) => acc + (p.fob || 0), 0),
      currency: values.currency,
      parcelOrdDoc: values.parcelOrdDoc,
      serviceType: values.serviceType,
      hasBattery: values.hasBattery,
      cityDestiny: isUS ? (destinationLocationData?.city || values.municipalityDestiny || "") : "",
      stateDestiny: isUS ? (destinationLocationData?.state || values.stateDestiny || "") : "",
      municipalityDestiny: isUS ? values.municipalityDestiny : "",
      packages: mappedPackages,
    };

    try {
      const results = await rateService.calculateRate(payload);
      onCalculate(results, formData);
    } catch (error) {
      console.error("Error calculating rate", error);
      onCalculate([], formData);
    }
  };

  const handleSwap = () => {
    setValues((prev: any) => ({
      ...prev,
      origin: prev.destination,
      destination: prev.origin,
    }));
  };

  const isPackagesValid = values.parcelOrdDoc === "DOC" ? true : packages.every(pkg =>
    pkg.length && Number(pkg.length) > 0 &&
    pkg.width && Number(pkg.width) > 0 &&
    pkg.height && Number(pkg.height) > 0 &&
    pkg.weight && Number(pkg.weight) > 0 &&
    pkg.fob !== "" && Number(pkg.fob) >= 0
  );

  const isUS = values.destination === "US";

  // Check validity for button disable
  const isFormValid =
    Boolean(values.destination) &&
    (values.parcelOrdDoc === "DOC" || isPackagesValid) &&
    values.zipcodeOrigin &&
    values.addressLineOrigin &&
    (!isUS || (values.zipcodeDestination && values.addressLineDestination && !zipcodeDestinationApiError)) &&
    !errors.destination &&
    !zipcodeOriginApiError;


  const handleZipcodeOriginBlur = async () => {
    if (values.zipcodeOrigin) {
      const country = values.origin || "US";
      const zip = normalizeZip(values.zipcodeOrigin, country);
      setValues((prev: any) => ({ ...prev, zipcodeOrigin: zip }));

      if (!isValidZipFormat(zip, country)) {
        const errorMsg = getZipFormatErrorMessage(country);
        setZipcodeOriginApiError(errorMsg);
        setErrors((prevErrors: any) => ({
          ...prevErrors,
          zipcodeOrigin: errorMsg,
        }));
        return;
      }

      setZipcodeOriginApiError(null);
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        zipcodeOrigin: null,
      }));

      try {
        const location = await utilsService.getLocationByZipcode({
          iso2code: country,
          zipcode: zip,
        });

        if (location) {
          setOriginLocationData(location);
        }
      } catch (err) {
        console.warn("Non-blocking zip lookup notice:", err);
      }
    } else {
      setZipcodeOriginApiError(null);
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        zipcodeOrigin: validationRules.zipcodeOrigin(values.zipcodeOrigin),
      }));
    }
  };

  const handleZipcodeDestinationBlur = async () => {
    if (values.zipcodeDestination) {
      const country = values.destination || "US";
      const zip = normalizeZip(values.zipcodeDestination, country);
      setValues((prev: any) => ({ ...prev, zipcodeDestination: zip }));

      if (!isValidZipFormat(zip, country)) {
        const errorMsg = getZipFormatErrorMessage(country);
        setZipcodeDestinationApiError(errorMsg);
        setErrors((prevErrors: any) => ({
          ...prevErrors,
          zipcodeDestination: errorMsg,
        }));
        return;
      }

      setZipcodeDestinationApiError(null);
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        zipcodeDestination: null,
      }));

      try {
        const location = await utilsService.getLocationByZipcode({
          iso2code: country,
          zipcode: zip,
        });

        if (location) {
          setDestinationLocationData(location);
        }
      } catch (err) {
        console.warn("Non-blocking zip lookup notice:", err);
      }
    } else {
      setZipcodeDestinationApiError(null);
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        zipcodeDestination: validationRules.zipcodeDestination(values.zipcodeDestination),
      }));
    }
  };

  const handleAddressSelect = async (addressData: AddressComponents) => {
    const country = values.origin || "US";
    const zip = normalizeZip(addressData.zipCode || "", country);

    setValues((prev: any) => ({
      ...prev,
      addressLineOrigin: addressData.fullAddress,
      municipalityOrigin: addressData.municipality,
      zipcodeOrigin: zip || prev.zipcodeOrigin,
      cityOrigin: addressData.city || prev.cityOrigin,
      stateOrigin: addressData.state || prev.stateOrigin,
    }));

    setErrors((prevErrors: any) => ({
      ...prevErrors,
      addressLineOrigin: undefined,
      zipcodeOrigin: undefined,
    }));
    setZipcodeOriginApiError(null);

    if (zip) {
      try {
        const location = await utilsService.getLocationByZipcode({
          iso2code: country,
          zipcode: zip,
        });
        if (location) {
          setOriginLocationData(location);
        }
      } catch (err) {
        console.warn("Non-blocking zip lookup notice:", err);
      }
    }
  };

  const handleDestinationAddressSelect = async (addressData: AddressComponents) => {
    const country = values.destination || "US";
    const zip = normalizeZip(addressData.zipCode || "", country);

    setValues((prev: any) => ({
      ...prev,
      addressLineDestination: addressData.fullAddress,
      municipalityDestiny: addressData.municipality,
      zipcodeDestination: zip || prev.zipcodeDestination,
      cityDestiny: addressData.city || prev.cityDestiny,
      stateDestiny: addressData.state || prev.stateDestiny,
    }));

    setErrors((prevErrors: any) => ({
      ...prevErrors,
      addressLineDestination: undefined,
      zipcodeDestination: undefined,
    }));
    setZipcodeDestinationApiError(null);

    if (zip) {
      try {
        const location = await utilsService.getLocationByZipcode({
          iso2code: country,
          zipcode: zip,
        });
        if (location) {
          setDestinationLocationData(location);
        }
      } catch (err) {
        console.warn("Non-blocking zip lookup notice:", err);
      }
    }
  };

  return (
    <div className="calculator-form-wrapper">
      <form onSubmit={handleSubmit(handleCalculate)}>
        {/* Step 1: Origin and Destination */}
        <div className="step-container">
          <div className="step-header">
            <span className="step-number">1</span>
            <span className="step-label">¿Desde dónde envías?</span>
            <div className="step-line"></div>
          </div>

          <div className="form-section">
            <div className="grid-row-origin-dest">
              <div className="form-group custom-select-wrapper">
                <label htmlFor="origin">Origen</label>
                <select
                  id="origin"
                  name="origin"
                  value={values.origin}
                  onChange={handleChange}
                  disabled
                >
                  <option value="US">Estados Unidos</option>
                </select>
              </div>

              <button
                type="button"
                className="swap-button"
                onClick={handleSwap}
                disabled={true}
                title="Intercambiar"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 16V4M7 4L3 8M7 4L11 8M17 8V20M17 20L21 16M17 20L13 16" />
                </svg>
              </button>

              <div className="form-group custom-select-wrapper" style={{ position: "relative" }}>
                <label htmlFor="destination">Destino</label>
                <select
                  id="destination"
                  name="destination"
                  value={values.destination}
                  className={errors.destination ? "is-invalid" : ""}
                  onChange={(e) => {
                    handleChange(e);
                    // Reset destination address and zip when destination country changes
                    setValues((prev: any) => ({
                      ...prev,
                      zipcodeDestination: "",
                      addressLineDestination: "",
                      municipalityDestiny: "",
                    }));
                    setZipcodeDestinationApiError(null);
                  }}
                >
                  <option value="">Seleccionar País</option>
                  {receiverCountries.map((c) => (
                    <option key={c.id} value={c.iso2Country}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {errors.destination && (
                  <div className="invalid-feedback" style={{ position: "absolute", top: "100%", left: 0, display: "block", marginTop: "2px", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                    {errors.destination}
                  </div>
                )}
              </div>
            </div>

            <div className="grid-row-zipcodes" style={values.destination !== "US" ? { gridTemplateColumns: "1fr" } : {}}>
              <div className="form-group">
                <label htmlFor="zipcodeOrigin">Código Postal Origen</label>
                <input
                  type="text"
                  id="zipcodeOrigin"
                  name="zipcodeOrigin"
                  placeholder="Ej: 33101"
                  value={values.zipcodeOrigin}
                  onChange={handleChange}
                  onBlur={handleZipcodeOriginBlur}
                  className={errors.zipcodeOrigin || zipcodeOriginApiError ? "is-invalid" : ""}
                />
                {(errors.zipcodeOrigin || zipcodeOriginApiError) && (
                  <div className="invalid-feedback" style={{ display: "block" }}>
                    {errors.zipcodeOrigin || zipcodeOriginApiError}
                  </div>
                )}
              </div>
              {values.destination === "US" && (
                <div className="form-group">
                  <label htmlFor="zipcodeDestination">Código Postal Destino</label>
                  <input
                    type="text"
                    id="zipcodeDestination"
                    name="zipcodeDestination"
                    placeholder="Ej: 1010"
                    value={values.zipcodeDestination}
                    onChange={handleChange}
                    onBlur={handleZipcodeDestinationBlur}
                    className={errors.zipcodeDestination || zipcodeDestinationApiError ? "is-invalid" : ""}
                  />
                  {(errors.zipcodeDestination || zipcodeDestinationApiError) && (
                    <div className="invalid-feedback" style={{ display: "block" }}>
                      {errors.zipcodeDestination || zipcodeDestinationApiError}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="form-group mt-3">
              <label htmlFor="addressLineOrigin">Dirección de Origen</label>
              <AddressAutocomplete
                value={values.addressLineOrigin}
                onChange={(val) => handleChange({ target: { name: 'addressLineOrigin', value: val } } as any)}
                onAddressSelect={handleAddressSelect}
                placeholder="Calle, Ave, etc. (Origen)"
                inputName="addressLineOrigin"
                apiKey={GOOGLE_API_KEY}
                inputClassName={errors.addressLineOrigin ? "is-invalid" : ""}
                country={values.origin.toLowerCase()}
              />
              {errors.addressLineOrigin && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {errors.addressLineOrigin}
                </div>
              )}
            </div>

            {values.destination === "US" && (
              <div className="form-group mt-3">
                <label htmlFor="addressLineDestination">Dirección de Destino</label>
                <AddressAutocomplete
                  value={values.addressLineDestination}
                  onChange={(val) => handleChange({ target: { name: 'addressLineDestination', value: val } } as any)}
                  onAddressSelect={handleDestinationAddressSelect}
                  placeholder="Calle, Ave, etc. (Destino)"
                  inputName="addressLineDestination"
                  apiKey={GOOGLE_API_KEY}
                  inputClassName={errors.addressLineDestination ? "is-invalid" : ""}
                  country={values.destination.toLowerCase()}
                />
                {errors.addressLineDestination && (
                  <div className="invalid-feedback" style={{ display: "block" }}>
                    {errors.addressLineDestination}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Step 2: Package Information */}
        <div className="step-container">
          <div className="step-header" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '15px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: '1 1 auto' }}>
              <span className="step-number">2</span>
              <span className="step-label">Detalles de tu paquete</span>
              <div className="step-line" style={{ marginRight: '15px' }}></div>
            </div>
            
            <div className="unit-switch-container" style={{
              display: 'flex',
              backgroundColor: 'var(--brand-black, #0a0a0a)',
              borderRadius: '8px',
              padding: '4px',
              height: '36px',
              alignItems: 'center',
              boxSizing: 'border-box',
              gap: '4px',
              width: '180px',
              flexShrink: 0
            }}>
              <button
                type="button"
                onClick={() => {
                  setValues((prev: any) => ({
                    ...prev,
                    unitSystem: 'imperial',
                    width: "",
                    height: "",
                    length: "",
                    weight: ""
                  }));
                  setErrors((prev: any) => ({
                    ...prev,
                    width: undefined,
                    height: undefined,
                    length: undefined,
                    weight: undefined
                  }));
                }}
                style={{
                  flex: 1,
                  height: '100%',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: values.unitSystem === 'imperial' ? '#222222' : 'transparent',
                  color: values.unitSystem === 'imperial' ? 'var(--brand-yellow, #ffd700)' : '#777777',
                  outline: 'none',
                  boxShadow: values.unitSystem === 'imperial' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : 'none'
                }}
              >
                Imperial
              </button>
              <button
                type="button"
                onClick={() => {
                  setValues((prev: any) => ({
                    ...prev,
                    unitSystem: 'metric',
                    width: "",
                    height: "",
                    length: "",
                    weight: ""
                  }));
                  setErrors((prev: any) => ({
                    ...prev,
                    width: undefined,
                    height: undefined,
                    length: undefined,
                    weight: undefined
                  }));
                }}
                style={{
                  flex: 1,
                  height: '100%',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: values.unitSystem === 'metric' ? '#222222' : 'transparent',
                  color: values.unitSystem === 'metric' ? 'var(--brand-yellow, #ffd700)' : '#777777',
                  outline: 'none',
                  boxShadow: values.unitSystem === 'metric' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : 'none'
                }}
              >
                Métrico
              </button>
            </div>
          </div>

          <div className="form-section">
            {/* Tipo de Contenido: Paquete / Documento */}
            <div className="form-group" style={{ marginBottom: '18px' }}>
              <label style={{ fontWeight: 600, marginBottom: '6px', display: 'block', color: 'var(--brand-black)' }}>Tipo de Paquete</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div
                  style={{
                    flex: '1 1 0%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1.5px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.25s ease',
                    outline: 'none',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onClick={() => {
                    setValues((prev: any) => ({
                      ...prev,
                      parcelOrdDoc: 'PARCEL',
                      width: '',
                      height: '',
                      length: '',
                      weight: '',
                      fob: '',
                      hasBattery: false,
                    }));
                    setErrors({});
                  }}
                >
                  <input type="checkbox" checked={values.parcelOrdDoc === 'PARCEL'} readOnly style={{ width: '18px', height: '18px', cursor: 'pointer', outline: 'none' }} />
                  <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#000000' }}>Caja</span>
                </div>
                <div
                  style={{
                    flex: '1 1 0%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1.5px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.25s ease',
                    outline: 'none',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onClick={() => {
                    setValues((prev: any) => ({
                      ...prev,
                      parcelOrdDoc: 'DOC',
                      width: '',
                      height: '',
                      length: '',
                      weight: '',
                      fob: '',
                      hasBattery: false,
                    }));
                    setErrors({});
                  }}
                >
                  <input type="checkbox" checked={values.parcelOrdDoc === 'DOC'} readOnly style={{ width: '18px', height: '18px', cursor: 'pointer', outline: 'none' }} />
                  <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#000000' }}>Documento</span>
                </div>
              </div>
                    {values.parcelOrdDoc === "DOC" ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '20px' }}>
                <div className="grid-row-dimensions">
                  <div className="form-group">
                    <label htmlFor="length">Largo ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                    <input
                      type="number"
                      min="0"
                      onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                      id="length"
                      name="length"
                      placeholder="Ej: 10"
                      value={values.length}
                      onChange={handleChange}
                      className={errors.length ? "is-invalid" : ""}
                    />
                    {errors.length && <div className="invalid-feedback">{errors.length}</div>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="width">Ancho ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                    <input
                      type="number"
                      min="0"
                      onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                      id="width"
                      name="width"
                      placeholder="Ej: 10"
                      value={values.width}
                      onChange={handleChange}
                      className={errors.width ? "is-invalid" : ""}
                    />
                    {errors.width && <div className="invalid-feedback">{errors.width}</div>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="height">Alto ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                    <input
                      type="number"
                      min="0"
                      onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                      id="height"
                      name="height"
                      placeholder="Ej: 10"
                      value={values.height}
                      onChange={handleChange}
                      className={errors.height ? "is-invalid" : ""}
                    />
                    {errors.height && <div className="invalid-feedback">{errors.height}</div>}
                  </div>
                </div>

                <div className="grid-row-details">
                <div className="form-group">
                  <label htmlFor="weight">Peso ({values.unitSystem === "metric" ? "kg" : "lbs"})</label>
                  <input
                    type="number"
                    min="0"
                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                    id="weight"
                    name="weight"
                    placeholder="Ej: 1"
                    value={values.weight}
                    onChange={handleChange}
                    className={errors.weight ? "is-invalid" : ""}
                  />
                  {errors.weight && <div className="invalid-feedback">{errors.weight}</div>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="fob">FOB ({values.currency})</label>
                  <input
                    type="number"
                    min="0"
                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                    id="fob"
                    name="fob"
                    placeholder="Ej: 100"
                    value={values.fob}
                    onChange={handleChange}
                    className={errors.fob ? "is-invalid" : ""}
                  />
                  {errors.fob && <div className="invalid-feedback">{errors.fob}</div>}
                </div>
                <div className="form-group">
                  <label htmlFor="currency">Moneda</label>
                  <select
                    id="currency"
                    name="currency"
                    value={values.currency}
                    onChange={handleChange}
                    disabled={currencies.length === 0}
                  >
                    {currencies.length > 0 ? (
                      currencies.map((curr) => (
                        <option key={curr.id} value={curr.isO3Currency}>
                          {curr.isO3Currency}
                        </option>
                      ))
                    ) : (
                      <option value="">Cargando...</option>
                    )}
                  </select>
                </div>
              </div>
            </div>
            ) : (
              <div style={{ marginTop: '16px' }}>
                {packages.map((pkg, idx) => (
                  <div key={idx} style={{ border: '1px solid #cbd5e1', borderRadius: '12px', padding: '16px', marginBottom: '16px', backgroundColor: '#ffffff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px', color: '#000000' }}>Pieza #{idx + 1}</span>
                      {packages.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemovePackage(idx)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          Eliminar
                        </button>
                      )}
                    </div>

                    <div className="grid-row-dimensions" style={{ marginBottom: '12px' }}>
                      <div className="form-group">
                        <label>Largo ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                        <input
                          type="number"
                          min="0"
                          onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                          value={pkg.length}
                          onChange={(e) => handlePackageChange(idx, 'length', e.target.value)}
                          placeholder="Ej: 10"
                        />
                      </div>
                      <div className="form-group">
                        <label>Ancho ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                        <input
                          type="number"
                          min="0"
                          onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                          value={pkg.width}
                          onChange={(e) => handlePackageChange(idx, 'width', e.target.value)}
                          placeholder="Ej: 10"
                        />
                      </div>
                      <div className="form-group">
                        <label>Alto ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                        <input
                          type="number"
                          min="0"
                          onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                          value={pkg.height}
                          onChange={(e) => handlePackageChange(idx, 'height', e.target.value)}
                          placeholder="Ej: 10"
                        />
                      </div>
                    </div>

                    <div className="grid-row-details">
                      <div className="form-group">
                        <label>Peso ({values.unitSystem === "metric" ? "kg" : "lbs"})</label>
                        <input
                          type="number"
                          min="0"
                          onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                          value={pkg.weight}
                          onChange={(e) => handlePackageChange(idx, 'weight', e.target.value)}
                          placeholder="Ej: 1"
                        />
                      </div>
                      <div className="form-group">
                        <label>FOB ({values.currency})</label>
                        <input
                          type="number"
                          min="0"
                          onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                          value={pkg.fob}
                          onChange={(e) => handlePackageChange(idx, 'fob', e.target.value)}
                          placeholder="Ej: 100"
                        />
                      </div>
                      <div className="form-group">
                        <label>Moneda</label>
                        <select
                          value={values.currency}
                          onChange={handleChange}
                          name="currency"
                          disabled={currencies.length === 0}
                        >
                          {currencies.length > 0 ? (
                            currencies.map((curr) => (
                              <option key={curr.id} value={curr.isO3Currency}>
                                {curr.isO3Currency}
                              </option>
                            ))
                          ) : (
                            <option value="">Cargando...</option>
                          )}
                        </select>
                      </div>
                    </div>

                    {values.parcelOrdDoc === "PARCEL" && (
                      <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id={`calc-isFragile-${idx}`}
                          checked={Boolean(pkg.isFragile)}
                          onChange={(e) => handlePackageChange(idx, 'isFragile', e.target.checked)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#000000' }}
                        />
                        <label htmlFor={`calc-isFragile-${idx}`} style={{ fontSize: '0.85rem', fontWeight: 600, color: '#000000', cursor: 'pointer', margin: 0 }}>
                          ¿Contenido Frágil?
                        </label>
                      </div>
                    )}
                  </div>
                ))}

                {packages.length < 10 && (
                  <button
                    type="button"
                    onClick={handleAddPackage}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      backgroundColor: '#f1f5f9',
                      color: '#334155',
                      border: '1.5px dashed #cbd5e1',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      width: '100%',
                      fontWeight: 600,
                      marginBottom: '20px',
                      outline: 'none'
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Agregar otra pieza
                  </button>
                )}
              </div>
            )}
            </div>

            {/* Batería — solo visual, no va al payload */}
            <div
              style={{
                marginTop: '12px',
                padding: '12px 16px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: values.parcelOrdDoc === 'DOC' ? 'not-allowed' : 'pointer',
                opacity: values.parcelOrdDoc === 'DOC' ? 0.6 : 1,
                userSelect: 'none',
                transition: 'all 0.25s ease',
                outline: 'none',
                WebkitTapHighlightColor: 'transparent'
              }}
              onClick={() => {
                if (values.parcelOrdDoc !== 'DOC') {
                  setValues((prev: any) => ({ ...prev, hasBattery: !prev.hasBattery }));
                }
              }}
            >
              <input
                type="checkbox"
                checked={values.hasBattery}
                disabled={values.parcelOrdDoc === 'DOC'}
                readOnly
                style={{ width: '18px', height: '18px', cursor: 'pointer', outline: 'none' }}
              />
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--brand-black)' }}>
                El paquete contiene baterías (ej. Laptops, teléfonos, etc.)
              </span>
            </div>
          </div>
        </div>

        {/* Step 3: Calculate Button */}
        <div className="step-container no-border">
          <div className="step-header">
            <span className="step-number">3</span>
            <span className="step-label">Calcular tarifa</span>
            <div className="step-line"></div>
          </div>
          <button
            type="submit"
            className={`${btnStyles.primary} ${btnStyles.fullWidth}`}
            disabled={!isFormValid}
            style={{ opacity: !isFormValid ? 0.7 : 1, cursor: !isFormValid ? 'not-allowed' : 'pointer' }}
          >
            <FontAwesomeIcon icon={faCalculator} className="mr-2" /> CALCULAR
          </button>
        </div>
      </form>
    </div>
  );
};

export default CalculatorForm;
